import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircleHeart, Send } from 'lucide-react';
import { toast } from 'sonner';

/** Question NPS (0-10) — max 1x/30j, jamais dans les 7 premiers jours d'un compte. */
export const NPSCard = ({ en, api }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [visible, setVisible] = useState(false);
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api('/api/tracking/nps-status').then((d) => setVisible(!!d.should_show)).catch(() => {});
  }, [api]);

  const dismiss = () => {
    setVisible(false);
    api('/api/tracking/nps/dismiss', { method: 'POST' }).catch(() => {});
  };

  const submit = async () => {
    if (score === null) return;
    setSending(true);
    try {
      await api('/api/tracking/nps', { method: 'POST', body: JSON.stringify({ score, comment }) });
      setSubmitted(true);
      toast.success(L('Merci pour votre retour !', 'Thanks for your feedback!'));
      setTimeout(() => setVisible(false), 2200);
    } catch { toast.error(L('Erreur, réessayez.', 'Error, please retry.')); }
    finally { setSending(false); }
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        className="w-full mb-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5 relative"
        data-testid="nps-card"
      >
        {!submitted && (
          <button onClick={dismiss} className="absolute top-3 right-3 text-zinc-500 hover:text-white transition-colors" data-testid="nps-dismiss-btn" aria-label="close">
            <X className="w-4 h-4" />
          </button>
        )}
        {submitted ? (
          <div className="flex items-center gap-3 py-2" data-testid="nps-thank-you">
            <MessageCircleHeart className="w-6 h-6 text-primary shrink-0" />
            <p className="text-sm text-white font-semibold">{L('Merci pour votre retour, ça compte pour nous !', 'Thanks for your feedback, it means a lot!')}</p>
          </div>
        ) : (
          <>
            <p className="text-white font-bold text-sm mb-3">
              {L('Sur 10, quelle est la probabilité que vous recommandiez Body-Up à un ami ?', 'On a scale of 0-10, how likely are you to recommend Body-Up to a friend?')}
            </p>
            <div className="flex gap-1.5 mb-3 flex-wrap" data-testid="nps-score-buttons">
              {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                <button
                  key={n} onClick={() => setScore(n)}
                  className={`w-8 h-8 rounded-lg text-xs font-bold flex items-center justify-center border transition-colors ${
                    score === n ? 'bg-primary text-white border-primary' : 'bg-black/30 text-zinc-400 border-white/10 hover:border-primary/40'
                  }`}
                  data-testid={`nps-score-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-zinc-500 mb-3 px-1">
              <span>{L('Pas du tout', 'Not at all')}</span>
              <span>{L('Totalement', 'Absolutely')}</span>
            </div>
            {score !== null && (
              <textarea
                value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder={L('Un commentaire ? (facultatif)', 'A comment? (optional)')}
                className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-500 mb-3 resize-none focus:outline-none focus:border-primary/40"
                rows={2} data-testid="nps-comment-input"
              />
            )}
            <button
              onClick={submit} disabled={score === null || sending}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-primary disabled:opacity-40"
              data-testid="nps-submit-btn"
            >
              <Send className="w-3.5 h-3.5" /> {L('Envoyer', 'Send')}
            </button>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default NPSCard;
